import type { PostTypeSummaryDto, VehicleTypeSummaryDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE REQUIREMENT COLUMN SET. ONE DERIVATION, TWO CONSUMERS.
 *
 *  ⚠️ P3'S REAL REQUIREMENT GRID MUST BUILD ITS COLUMNS FROM THIS FUNCTION.
 *  If you are writing a second derivation, stop. The whole point of the policy builder's
 *  live preview is that it shows what a CP nodal will actually see, and it only keeps
 *  saying anything true for as long as both screens compute it the same way. Two
 *  implementations drift within a sprint, and the drift is invisible — the preview keeps
 *  rendering, it just stops being a preview.
 *
 *  ⚠️ COMMAND ROLES ARE NOT A PARAMETER OF THIS FUNCTION, AND THAT IS THE POINT.
 *  FR-5.2.6b — command roles have no count, no allocation, no fulfilment percentage and
 *  no shortfall — is therefore enforced by the SIGNATURE rather than by an `if` somebody
 *  can delete. There is no argument through which a command role could become a column.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type RequirementColumn =
  | { kind: 'post'; id: string; label: string; fullLabel: string }
  | { kind: 'vehicle'; id: string; label: string; fullLabel: string; isWildcard: boolean };

export type RequirementColumnsInput = {
  /** ⚠️ THE POLICY'S OWN ORDER. Never re-sorted by the master's `displayOrder`. */
  postTypeIds: readonly string[];
  postTypes: ReadonlyMap<string, PostTypeSummaryDto>;
  vehicleTypeIds: readonly string[];
  vehicleTypes: ReadonlyMap<string, VehicleTypeSummaryDto>;
};

/**
 * Post columns in the policy's order, then the vehicle block.
 *
 * The master's `displayOrder` orders the PICKER; the policy's array orders the GRID.
 * Two different jobs — re-sorting here would silently discard the arrangement the
 * administrator built, and the preview would show them a policy they did not write.
 *
 * An id with no master row is DROPPED rather than rendered as a blank column: a hole in
 * a header row reads as a bug in the grid, whereas one fewer column reads as what it is,
 * a reference that no longer resolves.
 */
export const requirementColumns = (input: RequirementColumnsInput): RequirementColumn[] => {
  const posts = input.postTypeIds.flatMap<RequirementColumn>((id) => {
    const postType = input.postTypes.get(id);
    if (!postType) return [];
    return [
      {
        kind: 'post',
        id,
        // `shortName` is what PA_REQUIREMENTS §6.1's own mock uses — "SI/Sgt/Sub",
        // "ASI/A-ASI", "UB Men" — and it is also what makes the preview fit.
        label: postType.shortName || postType.name,
        fullLabel: postType.name,
      },
    ];
  });

  const vehicles = input.vehicleTypeIds.flatMap<RequirementColumn>((id) => {
    const vehicleType = input.vehicleTypes.get(id);
    if (!vehicleType) return [];
    return [
      {
        kind: 'vehicle',
        id,
        label: vehicleType.code || vehicleType.name,
        fullLabel: vehicleType.name,
        // Carried through so a consumer can mark the wildcard WITHOUT comparing names.
        isWildcard: vehicleType.isWildcard,
      },
    ];
  });

  // The vehicle block is always last: the grid reads force first, then transport.
  return [...posts, ...vehicles];
};

/** Where the vehicle block starts, for the group header and the dividing rule. */
export const firstVehicleIndex = (columns: readonly RequirementColumn[]): number =>
  columns.findIndex((column) => column.kind === 'vehicle');
